<template>
    <Teleport to="body">
        <Transition name="ui-dialog">
            <div
                v-if="isOpen"
                role="alertdialog"
                aria-modal="true"
                class="ui-dialog alert-dialog ui-dialog--nu"
                :aria-labelledby="dialogOptions?.title ? 'ui-dialog-title' : undefined"
                :aria-describedby="dialogOptions?.message ? 'ui-dialog-desc' : undefined"
                tabindex="-1"
                @keydown.escape="onClose"
            >
                <div class="ui-dialog__backdrop" aria-hidden="true" @click="onClose" />
                <div class="ui-dialog__panel">
                    <h2 v-if="dialogOptions?.title" id="ui-dialog-title" class="ui-dialog__title">
                        {{ dialogOptions.title }}
                    </h2>
                    <p v-if="dialogOptions?.message" id="ui-dialog-desc" class="ui-dialog__desc">
                        {{ dialogOptions.message }}
                    </p>
                    <div class="ui-dialog__actions">
                        <button
                            v-if="dialogOptions?.closeMessage"
                            @click="onClose"
                            type="button"
                            class="ui-dialog__btn ui-dialog__btn--ghost"
                        >
                            {{ dialogOptions.closeMessage }}
                        </button>
                        <button
                            v-if="dialogOptions?.confirmMessage"
                            @click="onConfirm"
                            type="button"
                            class="ui-dialog__btn ui-dialog__btn--primary"
                        >
                            {{ dialogOptions.confirmMessage }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script lang="ts" setup>
const { isOpen, dialogOptions, onConfirm, onClose } = useDialog();
</script>

<style lang="scss" scoped>
.ui-dialog {
    position: fixed;
    inset: 0;
    z-index: 100000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.ui-dialog__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    backdrop-filter: saturate(140%) blur(10px);
    -webkit-backdrop-filter: saturate(140%) blur(10px);
    animation: ui-dialog-fade-in 0.25s var(--ease-out-soft);
}

.ui-dialog__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 440px;
    padding: 24px;
    border-radius: var(--radius-2xl);
    background: var(--glass-bg-strong);
    backdrop-filter: var(--glass-blur-strong);
    -webkit-backdrop-filter: var(--glass-blur-strong);
    border: var(--glass-border);
    box-shadow: var(--shadow-xl);
    color: var(--surface-fg);
    animation: ui-dialog-panel-in 0.35s var(--ease-spring);

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0) 30%);
        pointer-events: none;
    }
}

.ui-dialog__title {
    position: relative;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.018em;
    margin-bottom: 10px;
    color: var(--surface-fg);
}

.ui-dialog__desc {
    position: relative;
    font-size: 14px;
    line-height: 1.5;
    color: var(--surface-muted);
    margin-bottom: 20px;
}

.ui-dialog__actions {
    position: relative;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
}

.ui-dialog__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    padding: 0 18px;
    border-radius: var(--radius-md);
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: -0.005em;
    transition: all var(--motion-fast) var(--ease-spring);

    &--ghost {
        color: var(--surface-fg);
        background: rgba(255, 255, 255, 0.6);
        border: 1px solid rgba(15, 23, 42, 0.1);

        &:hover {
            background: white;
            border-color: rgba(15, 23, 42, 0.18);
            transform: translateY(-1px);
            box-shadow: var(--shadow-sm);
        }
    }

    &--primary {
        color: white;
        background: var(--gradient-brand);
        border: 1px solid color-mix(in srgb, var(--color-primary-700) 35%, transparent);
        box-shadow:
            0 6px 14px color-mix(in srgb, var(--color-primary-500) 28%, transparent),
            inset 0 1px 0 rgba(255, 255, 255, 0.22);

        &:hover {
            transform: translateY(-1px);
            box-shadow:
                0 10px 20px color-mix(in srgb, var(--color-primary-500) 35%, transparent),
                inset 0 1px 0 rgba(255, 255, 255, 0.25);
        }
    }

    &:active {
        transform: scale(0.97);
    }

    &:focus-visible {
        outline: none;
        box-shadow: var(--shadow-glow);
    }
}

/* ============ Nouvelle coque : design nu (surface plate + magenta) ============ */
.ui-dialog--nu {
    .ui-dialog__panel {
        background: var(--nu-surface, #fff);
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        border: 1px solid var(--nu-line, #e4e7ec);
        border-radius: var(--nu-radius, 14px);
        box-shadow: var(--nu-shadow, 0 12px 32px rgba(15, 23, 42, 0.14));

        &::before {
            display: none;
        }
    }
    .ui-dialog__title {
        color: var(--nu-ink, #171a1f);
    }
    .ui-dialog__desc {
        color: var(--nu-ink-soft, #4a4f58);
    }
    .ui-dialog__btn {
        border-radius: var(--nu-radius-sm, 10px);
    }
    .ui-dialog__btn--ghost {
        background: var(--nu-surface-2, #f6f7f9);
        color: var(--nu-ink, #171a1f);
        border: 1px solid var(--nu-line, #e4e7ec);

        &:hover {
            background: #fff;
            border-color: var(--nu-magenta, #ec1e78);
        }
    }
    .ui-dialog__btn--primary {
        background: var(--nu-magenta, #ec1e78);
        border: 1px solid var(--nu-magenta, #ec1e78);
        box-shadow: none;

        &:hover {
            box-shadow: 0 6px 16px color-mix(in srgb, var(--nu-magenta, #ec1e78) 30%, transparent);
        }
    }
}

@keyframes ui-dialog-fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes ui-dialog-panel-in {
    from {
        opacity: 0;
        transform: translateY(12px) scale(0.96);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.ui-dialog-enter-active,
.ui-dialog-leave-active {
    transition: opacity var(--motion-base) ease;
}

.ui-dialog-enter-from,
.ui-dialog-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .ui-dialog-enter-active,
    .ui-dialog-leave-active,
    .ui-dialog__backdrop,
    .ui-dialog__panel {
        transition: none;
        animation: none;
    }
}
</style>
